import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Eye, EyeOff, Sprout, Heart } from "lucide-react";

const BG_URL = "https://images.unsplash.com/photo-1685563598694-28eec6739b23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwYWdyaWN1bHR1cmUlMjBmaWVsZHxlbnwwfHx8fDE3NzQ2NDYyMjh8MA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (Array.isArray(detail)) setError(detail.map((d) => d.msg || JSON.stringify(d)).join(" "));
      else setError("Login failed. Please check your credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 relative items-end p-12"
        style={{ backgroundImage: `url(${BG_URL})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A2E20]/80 via-[#1A2E20]/30 to-transparent" />
        <div className="relative z-10 text-white">
          <h2 className="text-3xl font-bold font-['Manrope'] mb-2">Agriculture Billing Made Simple</h2>
          <p className="text-white/70 text-base">GST invoicing, inventory, stock registers & bookkeeping for agri dealers across India.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-[#F8F9F6] bg-pattern">
        <Card className="w-full max-w-md border-[#DCE6DD] shadow-[0_4px_20px_-4px_rgba(45,84,56,0.08)]">
          <CardHeader className="text-center pb-2 pt-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#2D5438] flex items-center justify-center">
              <Sprout size={32} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#2D5438] font-['Manrope']">SuperAgri</h1>
            <p className="text-sm text-[#6B8573] mt-1">Sign in to your account</p>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              {error && (
                <div data-testid="login-error" className="p-3 text-sm text-red-700 bg-red-50 rounded-lg border border-red-100">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#1A2E20] font-medium">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-[#DCE6DD] focus:ring-[#2D5438]/20 focus:border-[#2D5438]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1A2E20] font-medium">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="border-[#DCE6DD] focus:ring-[#2D5438]/20 focus:border-[#2D5438] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B8573] hover:text-[#2D5438]"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Button
                data-testid="login-submit-btn"
                type="submit"
                disabled={submitting}
                className="w-full bg-[#2D5438] hover:bg-[#203D28] text-white h-11 transition-all duration-200 hover:-translate-y-[1px] shadow-sm"
              >
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
            <div className="mt-6 text-center space-y-2">
              <p className="text-xs text-[#6B8573] flex items-center justify-center gap-1">Made with <Heart size={11} className="text-red-500 fill-red-500" /> by Pavan Yerla for Bharat</p>
              <p className="text-[10px] text-[#6B8573]">&copy; 2026 SuperAgri.in — All rights reserved</p>
              <div className="flex items-center justify-center gap-3 text-[10px]">
                <Link to="/privacy" className="text-[#6B8573] hover:text-[#2D5438] underline">Privacy Policy</Link>
                <Link to="/terms" className="text-[#6B8573] hover:text-[#2D5438] underline">Terms & Conditions</Link>
                <Link to="/refund" className="text-[#6B8573] hover:text-[#2D5438] underline">Refund Policy</Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
